<template>
  <div class="main-screen">
    <!-- Daily Check-in Button -->
    <button 
      class="floating-daily-button" 
      @click="showDailyCheckin = true"
      title="Ежедневный вход"
    >
      <img :src="dailyIcon" alt="Daily Check-in" class="daily-icon" />
      <div v-if="!isTodayClaimed" class="daily-badge"></div>
    </button>
    
    <!-- Daily Check-in Modal -->
    <DailyCheckinModal 
      :is-visible="showDailyCheckin" 
      @close="showDailyCheckin = false"
      @claimed="handleDailyReward"
    />
    
    <!-- Balance Display -->
    <div class="balance-display top">
      <div class="balance-content">
        <img :src="currentCoinImage" alt="Coin" class="coin-icon compact" />
        <h1>{{ formatDisplay(currentBalance) }}</h1>
      </div>
    </div>
    
    <!-- Level Progress Bar -->
    <LevelProgressBar 
      :current-coins="totalCoinsEarned"
      :level-table="levelTable"
    />
    
    <!-- Character display -->
    <CharacterDisplay :current-coin-image="currentCoinImage" @tap="handleTap" />
        
    <!-- Upgrade Window -->
    <UpgradeWindow 
      :balance="currentBalance"
      :energy-current="energyCurrent"
      :energy-max="energyMax"
      :click-upgrade-cost="clickUpgradeCost"
      :energy-upgrade-cost="energyUpgradeCost"
      :auto-tap-cost="autoTapCost"
      :energy-refill-cost="energyRefillCost"
      :auto-tap-level="autoTapLevel"
      :max-auto-tap-level="maxAutoTapLevel"
      :boost-active="boostActive"
      :boost-expire-at="boostExpireAt"
      :boost-cooldown-end="boostCooldownEnd"
      @upgrade-click="upgradeClickPower"
      @upgrade-energy="upgradeEnergy"
      @upgrade-auto-tap="upgradeAutoTap"
      @refill-energy="refillEnergy"
      @activate-boost="activateBoost"
    />
    
    <!-- Notifications -->
    <AppNotification />
    
    <!-- Level Up Modal -->
    <LevelUpModal 
      :show="showLevelUpModal"
      :new-level="newLevelReached"
      :reward="levelUpReward"
      @close="handleLevelUpClose"
    />
    
    <!-- Offline Stats Modal -->
    <OfflineStatsModal 
      :show="showOfflineModal"
      :stats="offlineStats"
      @close="showOfflineModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { LEVELS_CONFIG } from '@/levels-config.js';
import CharacterDisplay from '@/components/CharacterDisplay.vue';
import UpgradeWindow from '@/components/UpgradeWindow.vue';
import LevelProgressBar from '@/components/LevelProgressBar.vue';
import LevelUpModal from '@/components/LevelUpModal.vue';
import DailyCheckinModal from '@/components/DailyCheckinModal.vue';
import OfflineStatsModal from '@/components/OfflineStatsModal.vue';
import AppNotification from '@/components/Notification.vue';

// Import coin images
import MonetaPng from '@/assets/Moneta.png';
import onePng from '@/assets/one.png';
import twoPng from '@/assets/two.png';
import threePng from '@/assets/three.png';
import fourPng from '@/assets/four.png';
import fivePng from '@/assets/five.png';
import SixPng from '@/assets/Six.png';
import sevenPng from '@/assets/seven.png';
import eightPng from '@/assets/eight.png';
import dailyIconPng from '@/assets/kd.png';

export default {
  name: 'MainScreen',
  props: {
    onViewChanged: {
      type: Function,
      required: true
    },
    appBalance: {
      type: Number,
      required: true
    },
    onAddClick: {
      type: Function,
      required: true
    }
  },
  emits: ['viewChanged', 'addClick', 'balanceUpdated', 'totalCoinsEarnedUpdated'],
  components: {
    CharacterDisplay,
    UpgradeWindow,
    LevelProgressBar,
    LevelUpModal,
    DailyCheckinModal,
    OfflineStatsModal,
    AppNotification
  },
  setup(props, { emit }) {
    // Game state
    const balance = ref(0);
    
    // Watch for changes in appBalance prop and update local balance
    watch(() => props.appBalance, (newBalance) => {
      balance.value = newBalance;
    });
    const tapPower = ref(1);
    const energyCurrent = ref(500);
    const energyMax = ref(500);
    const energyRefillRate = ref(1); // 1 energy per second
    const lastVisit = ref(Date.now());
    const totalCoinsEarned = ref(0);
    const autoTapInterval = ref(null);
    const autoTapLevel = ref(0);
    const autoTapIntervals = [18000, 16000, 14000, 13000, 12000, 11000, 10500, 10000]; // 18s, 16s, 14s, 13s, 12s, 11s, 10.5s, 10s
    const autoTapCosts = [100000, 250000, 500000, 1000000, 2000000, 4500000, 9000000, 20000000];
    const maxAutoTapLevel = 8;
    const boostActive = ref(false);
    const boostExpireAt = ref(null);
    const boostMultiplier = ref(2);
    const boostCooldownEnd = ref(null);

    // UI state
    const showDailyCheckin = ref(false);
    const showLevelUpModal = ref(false);
    const newLevelReached = ref({ level: 1, name: 'Wooden Coin' });
    const levelUpReward = ref(0);
    const showOfflineModal = ref(false);
    const offlineStats = ref({});
    
    
    // Level table
    const levelTable = LEVELS_CONFIG;
    
    // Images
    const dailyIcon = dailyIconPng;
    
    // Coin images mapping
    const coinImages = {
      1: onePng,
      2: twoPng,
      3: threePng,
      4: fourPng,
      5: fivePng,
      6: SixPng,
      7: sevenPng,
      8: eightPng,
    };
    
    // Calculate upgrade cost based on specific growth factors per level range to match requirements
    const calculateDynamicCost = (currentLevel) => {
      if (currentLevel < 1) return 500;
      
      // Predefined costs for early levels to match requirements
      if (currentLevel === 1) return 500;
      else if (currentLevel === 2) return 725;
      else if (currentLevel === 3) return 1050;
      else if (currentLevel === 4) return 1500;
      else if (currentLevel === 5) return 2200;
      else if (currentLevel === 10) return 12000;  // Approximate
      else if (currentLevel === 20) return 180000; // Approximate
      else if (currentLevel === 30) return 1200000; // Approximate
      else if (currentLevel === 40) return 6500000; // Approximate
      else if (currentLevel === 50) return 22000000; // Exact requirement
      else if (currentLevel === 60) return 55000000; // Approximate
      else if (currentLevel === 70) return 120000000; // Approximate
      else if (currentLevel === 80) return 260000000; // Approximate
      else if (currentLevel === 90) return 480000000; // Approximate
      else if (currentLevel === 99) return 750000000; // Exact requirement
      
      // For other levels, use interpolation between known points
      if (currentLevel < 10) {
        // Between level 5 and 10
        // We know: level 5 -> 2200, level 10 -> 12000
        const ratio = (currentLevel - 5) / (10 - 5);
        const logStart = Math.log(2200);
        const logEnd = Math.log(12000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 20) {
        // Between level 10 and 20
        // We know: level 10 -> 12000, level 20 -> 180000
        const ratio = (currentLevel - 10) / (20 - 10);
        const logStart = Math.log(12000);
        const logEnd = Math.log(180000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 30) {
        // Between level 20 and 30
        // We know: level 20 -> 180000, level 30 -> 1200000
        const ratio = (currentLevel - 20) / (30 - 20);
        const logStart = Math.log(180000);
        const logEnd = Math.log(1200000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 40) {
        // Between level 30 and 40
        // We know: level 30 -> 1200000, level 40 -> 6500000
        const ratio = (currentLevel - 30) / (40 - 30);
        const logStart = Math.log(1200000);
        const logEnd = Math.log(6500000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 50) {
        // Between level 40 and 50
        // We know: level 40 -> 6500000, level 50 -> 22000000
        const ratio = (currentLevel - 40) / (50 - 40);
        const logStart = Math.log(6500000);
        const logEnd = Math.log(22000000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 60) {
        // Between level 50 and 60
        // We know: level 50 -> 22000000, level 60 -> 55000000
        const ratio = (currentLevel - 50) / (60 - 50);
        const logStart = Math.log(22000000);
        const logEnd = Math.log(55000000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 70) {
        // Between level 60 and 70
        // We know: level 60 -> 55000000, level 70 -> 120000000
        const ratio = (currentLevel - 60) / (70 - 60);
        const logStart = Math.log(55000000);
        const logEnd = Math.log(120000000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 80) {
        // Between level 70 and 80
        // We know: level 70 -> 120000000, level 80 -> 260000000
        const ratio = (currentLevel - 70) / (80 - 70);
        const logStart = Math.log(120000000);
        const logEnd = Math.log(260000000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 90) {
        // Between level 80 and 90
        // We know: level 80 -> 260000000, level 90 -> 480000000
        const ratio = (currentLevel - 80) / (90 - 80);
        const logStart = Math.log(260000000);
        const logEnd = Math.log(480000000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else if (currentLevel < 100) {
        // Between level 90 and 100
        // We know: level 90 -> 480000000, level 100 -> 750000000
        const ratio = (currentLevel - 90) / (100 - 90);
        const logStart = Math.log(480000000);
        const logEnd = Math.log(750000000);
        return Math.round(Math.exp(logStart + ratio * (logEnd - logStart)));
      } else {
        // For levels above 100, continue with the pattern
        // Use the growth factor of approximately 1.05 per level
        const base = 750000000; // Cost at level 100
        const factor = 1.05; // Growth factor for higher levels
        return Math.round(base * Math.pow(factor, currentLevel - 100));
      }
    };
    
    // Calculate energy upgrade cost using the same formula
    const calculateEnergyCost = (currentLevel) => {
      return calculateDynamicCost(currentLevel);
    };
    
    // Computed values
    const clickUpgradeCost = computed(() => {
      return calculateDynamicCost(tapPower.value);
    });
    
    const energyUpgradeCost = computed(() => {
      return calculateEnergyCost(energyMax.value / 500); // Energy upgrades every 500 units
    });
    
    const autoTapCost = computed(() => {
      if (autoTapLevel.value >= autoTapCosts.length) return Infinity;
      return autoTapCosts[autoTapLevel.value];
    });
    
    const energyRefillCost = computed(() => {
      return 7500; // Фиксированная цена 7500 монет за пополнение энергии
    });
    
    const currentBalance = computed(() => {
      return balance.value;
    });
    
    const currentCoinImage = computed(() => {
      // Determine current coin image based on current level
      let currentLevel = levelTable.findLast(level => totalCoinsEarned.value >= level.tapsRequired);
      
      // If no level found (user has less than level 1 requirement), default to level 1
      if (!currentLevel) {
        currentLevel = levelTable[0];
      }
      
      if (currentLevel && currentLevel.level >= 1 && currentLevel.level <= 8) {
        return coinImages[currentLevel.level];
      } else {
        return MonetaPng;
      }
    });
    
    const isTodayClaimed = computed(() => {
      const today = new Date().toDateString();
      const lastClaimDate = localStorage.getItem('lastDailyClaimDate');
      return lastClaimDate === today;
    });
    
    // Formatting function for display
    const formatDisplay = (num) => {
      if (typeof num !== 'number') num = Number(num) || 0;
      // Format as whole number without decimals
      return Math.floor(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    
    // Energy refill function
    const refillEnergy = () => {
      if (balance.value >= energyRefillCost.value) {
        balance.value -= energyRefillCost.value;
        energyCurrent.value = energyMax.value;
        saveGame();
        
        // Emit balance and total coins earned updates to parent
        emit('balanceUpdated', balance.value);
        emit('totalCoinsEarnedUpdated', totalCoinsEarned.value);
      } else {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show("Ошибка", "Недостаточно монет для пополнения энергии", 'error');
        }
      }
    };
    
    // Handle tap
    const handleTap = (count = 1) => {
      if (energyCurrent.value >= tapPower.value * count) {
        const tapAmount = tapPower.value * (boostActive.value ? boostMultiplier.value : 1) * count;
        balance.value += tapAmount;
        energyCurrent.value -= tapPower.value * count;
        totalCoinsEarned.value += tapAmount;
        
        // Check for level up
        checkLevelUp();
        
        // Save game
        saveGame();
        
        // Emit balance and total coins earned updates to parent
        emit('balanceUpdated', balance.value);
        emit('totalCoinsEarnedUpdated', totalCoinsEarned.value);
      } else {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show("Ошибка", "Недостаточно энергии", 'error');
        }
      }
    };
    
    // Upgrade functions
    const upgradeClickPower = () => {
      if (balance.value >= clickUpgradeCost.value) {
        balance.value -= clickUpgradeCost.value;
        tapPower.value += 1;
        saveGame();
        
        // Emit balance and total coins earned updates to parent
        emit('balanceUpdated', balance.value);
        emit('totalCoinsEarnedUpdated', totalCoinsEarned.value);
      } else {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show("Ошибка", "Недостаточно монет для улучшения", 'error');
        }
      }
    };
    
    const upgradeEnergy = () => {
      if (balance.value >= energyUpgradeCost.value) {
        balance.value -= energyUpgradeCost.value;
        energyMax.value += 500; // Increase max energy by 500
        energyCurrent.value += 500; // Restore current energy as well
        saveGame();
        
        // Emit balance and total coins earned updates to parent
        emit('balanceUpdated', balance.value);
        emit('totalCoinsEarnedUpdated', totalCoinsEarned.value);
      } else {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show("Ошибка", "Недостаточно монет для улучшения", 'error');
        }
      }
    };
    
    const upgradeAutoTap = () => {
      if (autoTapLevel.value >= maxAutoTapLevel) {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show("Ошибка", "Максимальный уровень автоклика", 'error');
        }
        return;
      }
      
      if (balance.value >= autoTapCost.value) {
        balance.value -= autoTapCost.value;
        autoTapLevel.value += 1;
        saveGame();
        
        // Restart auto-tap interval if it was running
        if (autoTapInterval.value) {
          clearInterval(autoTapInterval.value);
          startAutoTap();
        }
        
        // Emit balance and total coins earned updates to parent
        emit('balanceUpdated', balance.value);
        emit('totalCoinsEarnedUpdated', totalCoinsEarned.value);
      } else {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show("Ошибка", "Недостаточно монет для улучшения", 'error');
        }
      }
    };
    
    // Start auto-tap function
    const startAutoTap = () => {
      if (autoTapInterval.value) {
        clearInterval(autoTapInterval.value);
      }
      
      if (autoTapLevel.value > 0) {
        const intervalMs = autoTapIntervals[autoTapLevel.value - 1] || 10000; // Default to 10s if level not found
        
        autoTapInterval.value = setInterval(() => {
          if (energyCurrent.value > 0) {
            handleTap(1);
          }
        }, intervalMs);
      }
    };
    
    // Boost functions
    const activateBoost = () => {
      // Check if boost is currently active
      if (boostActive.value) {
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show(
            "Уже активировано", 
            "Буст уже активирован", 
            'info'
          );
        }
        return;
      }
      
      // Check if boost is on cooldown
      const storedCooldownEnd = parseInt(localStorage.getItem('boostCooldownEnd') || 0);
      if (Date.now() < storedCooldownEnd) {
        const remainingTime = storedCooldownEnd - Date.now();
        const minutes = Math.floor(remainingTime / 60000);
        const seconds = Math.floor((remainingTime % 60000) / 1000);
        const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        
        if (window.NotificationService && window.NotificationService.show) {
          window.NotificationService.show(
            "Уже активировано", 
            `Подождите ${timeString} для обратной активации`, 
            'info'
          );
        }
        return;
      }
      
      // Activate boost for 30 seconds
      boostActive.value = true;
      boostExpireAt.value = Date.now() + 30000; // 30 seconds
      
      // Set cooldown: 12 hours after boost ends
      const cooldownEnd = boostExpireAt.value + 43200000; // 12 hours = 43200000 ms
      boostCooldownEnd.value = cooldownEnd;
      localStorage.setItem('boostCooldownEnd', cooldownEnd.toString());
      
      // Clear boost after duration
      setTimeout(() => {
        boostActive.value = false;
        boostExpireAt.value = null;
      }, 30000);
      
      saveGame();
    };
    
    // Check for level up
    const checkLevelUp = () => {
      const currentLevel = levelTable.findLast(level => totalCoinsEarned.value >= level.tapsRequired);
      if (currentLevel && currentLevel.level > newLevelReached.value.level) {
        // Calculate reward (some percentage of total coins earned)
        const reward = Math.floor(totalCoinsEarned.value * 0.01); // 1% as reward
        levelUpReward.value = reward;
        newLevelReached.value = currentLevel;
        showLevelUpModal.value = true;
        balance.value += reward; // Add reward to balance
        totalCoinsEarned.value += reward; // Add reward to total
      }
    };
    
    // Handle level up close
    const handleLevelUpClose = (reward) => {
      showLevelUpModal.value = false;
      if (reward > 0) {
        balance.value += reward;
        totalCoinsEarned.value += reward;
        saveGame();
      }
    };
    
    // Handle daily reward
    const handleDailyReward = (reward) => {
      balance.value += reward;
      totalCoinsEarned.value += reward;
      saveGame();
    };
    
    // Energy regeneration
    const regenerateEnergy = () => {
      const now = Date.now();
      const timePassed = now - lastVisit.value;
      const energyRegenerated = Math.floor(timePassed * (energyRefillRate.value / 1000)); // Energy per second
      
      // Add regenerated energy but not more than max
      energyCurrent.value = Math.min(energyMax.value, energyCurrent.value + energyRegenerated);
      
      // Update last visit time
      lastVisit.value = now;
    };
    
    // Calculate offline earnings
    const calculateOfflineEarnings = () => {
      const now = Date.now();
      const timeOffline = now - lastVisit.value;
      
      if (timeOffline > 60000) { // More than 1 minute offline
        // Calculate energy regenerated offline
        const energyRegenerated = Math.floor(timeOffline * (energyRefillRate.value / 1000));
        energyCurrent.value = Math.min(energyMax.value, energyCurrent.value + energyRegenerated);
        
        // For simplicity, we'll just show a notification about offline time
        const minutesOffline = Math.floor(timeOffline / 60000);
        const hoursOffline = Math.floor(minutesOffline / 60);
        
        offlineStats.value = {
          timeOffline: timeOffline,
          hoursOffline: hoursOffline,
          minutesOffline: minutesOffline % 60,
          energyRegenerated: Math.min(energyMax.value - (energyMax.value - energyCurrent.value), energyRegenerated)
        };
        
        showOfflineModal.value = true;
      }
      
      // Update last visit time to now
      lastVisit.value = now;
    };
    
    // Save game function
    const saveGame = () => {
      const gameState = {
        balance: balance.value,
        tapPower: tapPower.value,
        energyCurrent: energyCurrent.value,
        energyMax: energyMax.value,
        lastVisit: lastVisit.value,
        totalCoinsEarned: totalCoinsEarned.value,
        autoTapLevel: autoTapLevel.value,
        boostActive: boostActive.value,
        boostExpireAt: boostExpireAt.value,
        boostCooldownEnd: boostCooldownEnd.value
      };
      
      localStorage.setItem('gameState', JSON.stringify(gameState));
    };
    
    // Load game function
    const loadGame = () => {
      const savedState = localStorage.getItem('gameState');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          // Load from saved state, but prioritize the appBalance prop if available
          balance.value = props.appBalance !== undefined && props.appBalance !== null ? props.appBalance : (parsedState.balance || 0);
          tapPower.value = parsedState.tapPower || 1;
          energyCurrent.value = parsedState.energyCurrent || 500;
          energyMax.value = parsedState.energyMax || 500;
          lastVisit.value = parsedState.lastVisit || Date.now();
          
          // Try to load total coins earned from app sync, fallback to saved state
          const appTotalCoins = localStorage.getItem('astro_total_coins_earned');
          totalCoinsEarned.value = appTotalCoins ? parseInt(appTotalCoins) : (parsedState.totalCoinsEarned || 0);
          
          autoTapLevel.value = parsedState.autoTapLevel || 0;
          boostActive.value = parsedState.boostActive || false;
          boostExpireAt.value = parsedState.boostExpireAt || null;
          boostCooldownEnd.value = parsedState.boostCooldownEnd || null;
          
          // Check if boost is still active
          if (boostActive.value && boostExpireAt.value && Date.now() > boostExpireAt.value) {
            boostActive.value = false;
            boostExpireAt.value = null;
            saveGame();
          }
          
          // Calculate offline earnings if player was away for a while
          calculateOfflineEarnings();
          
          // Check if user has reached a new level after loading
          checkLevelUp();
        } catch (e) {
          console.error('Error loading game state:', e);
        }
      } else {
        // Initialize with starting values if no save exists
        balance.value = props.appBalance !== undefined && props.appBalance !== null ? props.appBalance : 0;
        tapPower.value = 1;
        energyCurrent.value = 500;
        energyMax.value = 500;
        lastVisit.value = Date.now();
        
        // Try to load total coins earned from app sync, fallback to 0
        const appTotalCoins = localStorage.getItem('astro_total_coins_earned');
        totalCoinsEarned.value = appTotalCoins ? parseInt(appTotalCoins) : 0;
        
        autoTapLevel.value = 0;
        boostActive.value = false;
        boostExpireAt.value = null;
        boostCooldownEnd.value = null;
      }
    };
    
    // Initialize game
    onMounted(() => {
      loadGame();
      startAutoTap();
      
      // Check if we should show the daily check-in modal on first visit
      const hasSeenDailyCheckin = localStorage.getItem('has_seen_daily_checkin');
      if (!hasSeenDailyCheckin) {
        showDailyCheckin.value = true;
        localStorage.setItem('has_seen_daily_checkin', 'true');
      }
      
      // Set up energy regeneration interval
      setInterval(() => {
        // Small amount of energy regeneration
        if (energyCurrent.value < energyMax.value) {
          const now = Date.now();
          const timePassed = now - lastVisit.value;
          const energyToAdd = Math.floor(timePassed * (energyRefillRate.value / 1000));
          
          if (energyToAdd > 0) {
            energyCurrent.value = Math.min(energyMax.value, energyCurrent.value + energyToAdd);
            lastVisit.value = now; // Update last visit to now to avoid double counting
            saveGame();
          }
        }
      }, 1000); // Check every 1 second to match regeneration rate
    });
    
    onUnmounted(() => {
      if (autoTapInterval.value) {
        clearInterval(autoTapInterval.value);
      }
      saveGame();
    });
    
    return {
      // State
      balance,
      tapPower,
      energyCurrent,
      energyMax,
      totalCoinsEarned,
      autoTapLevel,
      maxAutoTapLevel,
      boostActive,
      boostExpireAt,
      boostCooldownEnd,
      
      // UI state
      showDailyCheckin,
      showLevelUpModal,
      newLevelReached,
      levelUpReward,
      showOfflineModal,
      offlineStats,
      
      // Computed
      clickUpgradeCost,
      energyUpgradeCost,
      autoTapCost,
      energyRefillCost,
      currentBalance,
      currentCoinImage,
      isTodayClaimed,
      
      // Functions
      handleTap,
      upgradeClickPower,
      upgradeEnergy,
      upgradeAutoTap,
      refillEnergy,
      activateBoost,
      handleLevelUpClose,
      handleDailyReward,
      formatDisplay,
      
      // Images
      dailyIcon,
      
      // Additional properties needed for template
      levelTable
    };
  }
};
</script>

<style scoped>
.main-screen {
  position: relative;
  width: 100%;
  height: 100vh;
  background: radial-gradient(circle at center, #000000, #1a1a1a);
  overflow-x: hidden;
  overflow-y: auto;
  font-family: 'Inter', 'Roboto', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 10px 120px 10px; /* Extra bottom padding for upgrade window */
}

.balance-display.top {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 20px;
  padding: 15px 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5),
              inset 0 0 15px rgba(128, 128, 128, 0.2);
}

.balance-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.balance-display h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.7);
  letter-spacing: 0.5px;
}

.coin-icon.compact {
  width: 28px;
  height: 28px;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.6));
}

.floating-daily-button {
  position: fixed;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 101;
  transition: all 0.3s ease;
}

.floating-daily-button:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 25px rgba(128, 128, 128, 0.6),
              0 0 30px rgba(128, 128, 128, 0.5);
}

.daily-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: none;
}

.daily-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  background: #808080;
  border-radius: 50%;
  border: 2px solid #000000;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .balance-display.top {
    top: 15px;
    padding: 12px 20px;
  }
  
  .balance-display h1 {
    font-size: 20px;
  }
  
  .coin-icon.compact {
    width: 24px;
    height: 24px;
  }
  
  .floating-daily-button {
    top: 15px;
    right: 15px;
    width: 45px;
    height: 45px;
  }
  
  .daily-icon {
    width: 20px;
    height: 20px;
  }
}
</style>